import React, { useState, useEffect, useRef } from 'react';

interface QuoteItem {
    id: number;
    item_key: string;
    unit_price: string;
    unit: string;
    remarks: string;
}

interface ProposalItem {
    id?: number;
    name: string;
    unit: string;
    quantity: number;
    unitPrice: number;
    total: number;
    remarks: string;
    isCustom?: boolean;
}

interface QuoteGeneratorProps {
    request: any;
    onClose: () => void;
    onSaveSuccess: () => void;
}

const DEFAULT_TERMS = `- 정산 리포트(2탭) 기본 제공: (1) 스케줄/증빙 (2) 비용(보관/작업/운송/기타)
- 작업(분류, E/P, 소분 등)은 실제 발생 수량 기준으로 과금됩니다.
- 운송 단가는 센터/차종/시간창/대기 여부에 따라 변동될 수 있습니다.
- 입금계좌: (주)천일국제물류 / IBK 475-050636-01-018
- 담당: 신혜정 / Tel: 070-4490-6145`;

const QuoteGenerator: React.FC<QuoteGeneratorProps> = ({ request, onClose, onSaveSuccess }) => {
    const [lineItems, setLineItems] = useState<ProposalItem[]>([]);

    // Core Fields
    const [remarks, setRemarks] = useState(DEFAULT_TERMS);
    const [vatIncluded, setVatIncluded] = useState(false); // Default to separate as per image "VAT 별도"
    const [isSaving, setIsSaving] = useState(false);

    // New Header Fields
    const [center, setCenter] = useState('');
    const [category, setCategory] = useState(request.sku || ''); // Use SKU as initial category?
    const [settlementMethod, setSettlementMethod] = useState('월 정산(2탭 리포트)');
    const [validity, setValidity] = useState('견적일로부터 1개월');

    // View Handling
    const [existingProposals, setExistingProposals] = useState<any[]>([]);
    const [viewMode, setViewMode] = useState<'create' | 'list'>('create');

    useEffect(() => {
        if (viewMode === 'create') {
            fetchQuoteItems();
        }
        fetchExistingProposals();
    }, [request.id, viewMode]);

    const fetchQuoteItems = async () => {
        try {
            const res = await fetch('/api/quote-items');
            if (res.ok) {
                const data: QuoteItem[] = await res.json();
                const initialItems: ProposalItem[] = data.map(item => ({
                    id: item.id,
                    name: item.item_key,
                    unit: item.unit || '',
                    quantity: 0,
                    unitPrice: parseInt((item.unit_price || '0').toString().replace(/[^0-9]/g, '')) || 0,
                    total: 0,
                    remarks: item.remarks || '',
                    isCustom: false
                }));
                setLineItems(initialItems);
            }
        } catch (e) {
            console.error(e);
        }
    };

    const fetchExistingProposals = async () => {
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`/api/admin/proposals/${request.id}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                setExistingProposals(data);
            }
        } catch (e) {
            console.error(e);
        }
    };

    const handleRowChange = (index: number, field: keyof ProposalItem, value: any) => {
        const newItems = [...lineItems];
        const item = newItems[index];

        if (field === 'quantity') item.quantity = parseInt(value) || 0;
        else if (field === 'unitPrice') item.unitPrice = parseInt(value) || 0;
        else if (field === 'name') item.name = value;
        else if (field === 'unit') item.unit = value;
        else if (field === 'remarks') item.remarks = value;

        item.total = item.quantity * item.unitPrice;
        setLineItems(newItems);
    };

    const addCustomRow = () => {
        setLineItems([...lineItems, {
            name: '',
            unit: '',
            quantity: 0,
            unitPrice: 0,
            total: 0,
            remarks: '',
            isCustom: true
        }]);
    };

    const removeRow = (index: number) => {
        const newItems = [...lineItems];
        newItems.splice(index, 1);
        setLineItems(newItems);
    };

    const calculateValues = () => {
        const subtotal = lineItems.reduce((acc, item) => acc + item.total, 0);
        const vat = vatIncluded ? Math.round(subtotal - (subtotal / 1.1)) : Math.round(subtotal * 0.1);
        // If Included: subtotal is Total. Supply = Total / 1.1. VAT = Total - Supply.
        // If Excluded: subtotal is Supply. Total = Supply * 1.1.

        let supplyPrice = subtotal;
        let total = subtotal + vat;

        if (vatIncluded) {
            total = subtotal;
            supplyPrice = subtotal - vat;
        }

        return { supplyPrice, vat, total };
    };

    const handleSave = async () => {
        const activeItems = lineItems.filter(i => i.quantity > 0);
        if (activeItems.length === 0) return alert('수량이 1 이상인 항목이 없습니다.');

        setIsSaving(true);
        try {
            const token = localStorage.getItem('token');
            const { supplyPrice, vat, total } = calculateValues();

            const metadata = { center, category, settlementMethod, validity };

            const payload = {
                requestId: request.id,
                items: { lineItems: activeItems, metadata }, // Wrap it! Schema is JSONB.
                totalAmount: total,
                vatIncluded,
                remarks
            };

            const res = await fetch('/api/admin/proposals', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            });
            if (res.ok) {
                alert('견적서가 저장되었습니다.');
                fetchExistingProposals();
                setViewMode('list');
            } else {
                alert('저장 실패');
            }
        } catch (e) {
            console.error(e);
            alert('오류 발생');
        } finally {
            setIsSaving(false);
        }
    };

    // Print Logic
    const handlePrint = (proposal?: any) => {
        let itemsToPrint: ProposalItem[] = [];
        let meta = { center, category, settlementMethod, validity };
        let remarksToPrint = remarks;
        let vatInc = vatIncluded;

        if (proposal) {
            const parsed = typeof proposal.items === 'string' ? JSON.parse(proposal.items) : proposal.items;
            if (parsed.lineItems) {
                itemsToPrint = parsed.lineItems;
                meta = parsed.metadata || meta;
            } else {
                itemsToPrint = Array.isArray(parsed) ? parsed : [];
            }
            remarksToPrint = proposal.remarks;
            vatInc = proposal.vat_included;
        } else {
            itemsToPrint = lineItems.filter(i => i.quantity > 0);
        }

        if (itemsToPrint.length === 0) return alert('출력할 항목이 없습니다.');

        const subtotal = itemsToPrint.reduce((acc, item) => acc + item.total, 0);
        const vat = vatInc ? Math.round(subtotal - (subtotal / 1.1)) : Math.round(subtotal * 0.1);
        const supplyPrice = vatInc ? subtotal - vat : subtotal;
        const total = vatInc ? subtotal : subtotal + vat;

        // Iframe Approach to avoid popup/about:blank issues
        const iframe = document.createElement('iframe');
        iframe.style.position = 'fixed'; // Fixed to avoid affecting layout
        iframe.style.right = '0';
        iframe.style.bottom = '0';
        iframe.style.width = '0px';
        iframe.style.height = '0px';
        iframe.style.border = 'none';
        document.body.appendChild(iframe);

        const doc = iframe.contentWindow?.document;
        if (!doc) return;

        doc.open();
        doc.write(`
            <html>
            <head>
                <title>코스트코 납품대행 견적서 - ${request.company_name}</title>
                <style>
                    @media print {
                        @page { 
                            size: A4 landscape; 
                            margin: 5mm; /* Reduced margins as requested */
                        }
                        body { 
                            margin: 0;
                            padding: 0;
                            -webkit-print-color-adjust: exact;
                            print-color-adjust: exact;
                        }
                        
                        /* Master Table Layout */
                        .master-table {
                            width: 100%;
                            border-collapse: collapse;
                            border: none;
                        }
                        
                        /* Header/Footer Layout */
                        .master-header-content {
                            height: 30mm;
                            display: flex;
                            justify-content: space-between;
                            align-items: flex-end; 
                            border-bottom: 2px solid #333; 
                            margin-bottom: 30px; /* Increased gap */
                            padding-bottom: 5px;
                        }

                        /* Footer Spacer (in tfoot) */
                        .master-footer-spacer {
                            height: 15mm; /* Reserve space for fixed footer */
                        }

                        /* Fixed Footer Text */
                        .fixed-footer-text {
                            position: fixed;
                            bottom: 5mm; /* Match margin or slightly above */
                            right: 5mm;
                            width: 100%;
                            text-align: right;
                            font-size: 12px;
                            color: #555;
                            font-weight: bold;
                            pointer-events: none; /* Just in case */
                        }

                        /* Ensure Thead/Tfoot Repeat */
                        thead { display: table-header-group; }
                        tfoot { display: table-footer-group; }
                        
                        .print-content { padding: 5px 0; }
                    }

                    body { 
                        font-family: 'Malgun Gothic', Dotum, sans-serif; 
                        margin: 0; 
                        padding: 0;
                        color: #000;
                    }

                    h1 { 
                        text-align: center; 
                        font-size: 28px; 
                        letter-spacing: 2px; 
                        margin: 0; 
                        font-weight: bold; 
                        flex: 1;
                    }
                    .header-logo { height: 40px; }

                    /* Data Tables */
                    .header-table { width: 100%; border-collapse: collapse; margin-bottom: 20px; font-size: 14px; border: 1px solid #ccc !important; }
                    .header-table td { border: 1px solid #ccc !important; padding: 6px 10px; height: 30px; }
                    .header-table .label { background-color: #f5f5f5 !important; font-weight: bold; text-align: center; width: 120px; }
                    .header-table .value { padding-left: 15px; }

                    .items-table { width: 100%; border-collapse: collapse; margin-top: 10px; font-size: 13px; }
                    .items-table th { background: #f5f5f5 !important; color: #000; padding: 8px 5px; text-align: center; border: 1px solid #ccc !important; font-weight: bold; }
                    .items-table td { border: 1px solid #ccc !important; padding: 6px 5px; text-align: center; }
                    .items-table td.left { text-align: left; padding-left: 10px; }
                    .items-table td.right { text-align: right; padding-right: 10px; }
                    
                    /* Summary & Remarks */
                    .summary-table { width: 300px; margin-left: auto; margin-top: 20px; border-collapse: collapse; font-size: 14px; border: 1px solid #ccc !important; }
                    .summary-table td { padding: 8px; border: 1px solid #ccc !important; text-align: center; }
                    .summary-table .label { background-color: #f5f5f5 !important; font-weight: bold; }
                    .summary-table .value { text-align: right; font-weight: bold; padding-right: 10px; }
                    .summary-table .total-row { background-color: #eee !important; color: #000; }

                    .remarks-box { margin-top: 40px; padding: 10px; font-size: 13px; line-height: 1.6; border: none; }
                    .remarks-title { font-weight: bold; margin-bottom: 10px; display: block; font-size: 16px; }
                    .remarks-content { white-space: pre-wrap; }
                </style>
            </head>
            <body>
                <!-- Fixed Footer (Outside Table flow) -->
                <div class="fixed-footer-text">
                    (주)천일국제물류 www.chunilkor.co.kr
                </div>

                <table class="master-table">
                    <thead>
                        <tr>
                            <td>
                                <div class="master-header-content">
                                    <img src="/logo.png" class="header-logo" alt="Logo Left" />
                                    <h1>코스트코 납품대행 견적서</h1>
                                    <!-- Right Logo Removed -->
                                </div>
                            </td>
                        </tr>
                    </thead>
                    <tfoot>
                        <tr>
                            <td>
                                <!-- Empty Spacer to prevent overlapping the Fixed Footer -->
                                <div class="master-footer-spacer"></div>
                            </td>
                        </tr>
                    </tfoot>
                    <tbody>
                        <tr>
                            <td>
                                <div class="print-content">
                                    
                                    <table class="header-table">
                                        <tr>
                                            <td class="label">견적일</td>
                                            <td class="value">${new Date().toLocaleDateString()}</td>
                                            <td class="label">밴더명</td>
                                            <td class="value">${request.company_name}</td>
                                        </tr>
                                        <tr>
                                            <td class="label">센터</td>
                                            <td class="value">${meta.center}</td>
                                            <td class="label">품목군</td>
                                            <td class="value">${meta.category}</td>
                                        </tr>
                                        <tr>
                                            <td class="label">정산방식</td>
                                            <td class="value">${meta.settlementMethod}</td>
                                            <td class="label">VAT</td>
                                            <td class="value">${vatInc ? '포함' : '별도(10%)'}</td>
                                        </tr>
                                        <tr>
                                            <td class="label">견적 유효기간</td>
                                            <td class="value" colspan="3">${meta.validity}</td>
                                        </tr>
                                    </table>

                                    <table class="items-table">
                                        <colgroup>
                                            <col style="width: 50px;">
                                            <col style="width: 250px;">
                                            <col style="width: 80px;">
                                            <col style="width: 80px;">
                                            <col style="width: 100px;">
                                            <col style="width: 100px;">
                                            <col>
                                        </colgroup>
                                        <thead>
                                            <tr>
                                                <th>No</th>
                                                <th>항목</th>
                                                <th>단위</th>
                                                <th>수량</th>
                                                <th>단가(원)</th>
                                                <th>금액(원)</th>
                                                <th>비고</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            ${itemsToPrint.map((item, idx) => `
                                                <tr>
                                                    <td>${idx + 1}</td>
                                                    <td class="left">${item.name}</td>
                                                    <td>${item.unit || '-'}</td>
                                                    <td>${item.quantity.toLocaleString()}</td>
                                                    <td class="right">${item.unitPrice.toLocaleString()}</td>
                                                    <td class="right">${item.total.toLocaleString()}</td>
                                                    <td class="left" style="font-size: 11px; color: #555;">${item.remarks || ''}</td>
                                                </tr>
                                            `).join('')}
                                        </tbody>
                                    </table>

                                    <div style="display: flex; justify-content: space-between; margin-top: 30px;">
                                        <div class="remarks-box" style="flex: 1; margin-top: 0; padding-right: 20px;">
                                            <span class="remarks-title">비고/조건</span>
                                            <div class="remarks-content">${remarksToPrint}</div>
                                        </div>

                                        <table class="summary-table" style="margin-top: 0; margin-left: 0;">
                                            <tr>
                                                <td class="label">공급가액</td>
                                                <td class="value">${supplyPrice.toLocaleString()}</td>
                                            </tr>
                                            <tr>
                                                <td class="label">부가세</td>
                                                <td class="value">${vat.toLocaleString()}</td>
                                            </tr>
                                            <tr class="total-row">
                                                <td style="font-weight: bold;">총 견적금액</td>
                                                <td style="font-weight: bold; text-align: right; padding-right: 10px;">${total.toLocaleString()}</td>
                                            </tr>
                                        </table>
                                    </div>

                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>
                
                <script>
                    window.onload = function() {
                        setTimeout(function() { 
                            window.print(); 
                        }, 500);
                    };
                </script>
            </body>
            </html>
        `);
        doc.close();

        // Clean up iframe after 1 minute (allowing time for print dialog interaction)
        setTimeout(() => {
            document.body.removeChild(iframe);
        }, 60000);
    };

    const { supplyPrice, vat, total } = calculateValues();

    return (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.5)', zIndex: 2000, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <div style={{ background: 'white', width: '1300px', height: '95vh', padding: '20px', borderRadius: '8px', display: 'flex', flexDirection: 'column' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                    <h2>코스트코 납품대행 견적서 작성</h2>
                    <button onClick={onClose} style={{ border: 'none', background: 'none', fontSize: '20px', cursor: 'pointer' }}>X</button>
                </div>

                <div style={{ marginBottom: '10px' }}>
                    <button
                        onClick={() => setViewMode('create')}
                        style={{ marginRight: '10px', fontWeight: viewMode === 'create' ? 'bold' : 'normal', borderBottom: viewMode === 'create' ? '2px solid blue' : 'none' }}>
                        작성
                    </button>
                    <button
                        onClick={() => setViewMode('list')}
                        style={{ fontWeight: viewMode === 'list' ? 'bold' : 'normal', borderBottom: viewMode === 'list' ? '2px solid blue' : 'none' }}>
                        발행 이력 ({existingProposals.length})
                    </button>
                </div>

                {viewMode === 'list' ? (
                    <div style={{ flex: 1, overflowY: 'auto' }}>
                        {existingProposals.length === 0 ? <p>이력 없음</p> : (
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                    <tr style={{ background: '#f8f9fa' }}>
                                        <th style={{ padding: '10px' }}>발행일</th>
                                        <th style={{ padding: '10px' }}>총액</th>
                                        <th style={{ padding: '10px' }}>관리</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {existingProposals.map((p) => (
                                        <tr key={p.id} style={{ borderBottom: '1px solid #eee' }}>
                                            <td style={{ padding: '10px', textAlign: 'center' }}>{new Date(p.created_at).toLocaleString()}</td>
                                            <td style={{ padding: '10px', textAlign: 'center' }}>{parseInt(p.total_amount).toLocaleString()}</td>
                                            <td style={{ padding: '10px', textAlign: 'center' }}>
                                                <button onClick={() => handlePrint(p)} style={{ padding: '5px 10px', background: '#17a2b8', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>출력</button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                ) : (
                    <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
                        {/* Header Inputs */}
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '10px', marginBottom: '15px', background: '#f1f3f5', padding: '15px', borderRadius: '5px' }}>
                            <div>
                                <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold' }}>업체명 (자동)</label>
                                <input type="text" value={request.company_name} disabled style={{ width: '100%', padding: '5px', background: '#e9ecef' }} />
                            </div>
                            <div>
                                <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold' }}>센터</label>
                                <input type="text" value={center} onChange={e => setCenter(e.target.value)} style={{ width: '100%', padding: '5px' }} placeholder="예: 평택" />
                            </div>
                            <div>
                                <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold' }}>품목군</label>
                                <input type="text" value={category} onChange={e => setCategory(e.target.value)} style={{ width: '100%', padding: '5px' }} />
                            </div>
                            <div>
                                <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold' }}>정산방식</label>
                                <input type="text" value={settlementMethod} onChange={e => setSettlementMethod(e.target.value)} style={{ width: '100%', padding: '5px' }} />
                            </div>
                            <div>
                                <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold' }}>유효기간</label>
                                <input type="text" value={validity} onChange={e => setValidity(e.target.value)} style={{ width: '100%', padding: '5px' }} />
                            </div>
                            <div>
                                <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold' }}>VAT 설정</label>
                                <select value={vatIncluded ? 'inc' : 'exc'} onChange={e => setVatIncluded(e.target.value === 'inc')} style={{ width: '100%', padding: '5px' }}>
                                    <option value="exc">별도 (10% 가산)</option>
                                    <option value="inc">포함 (내장됨)</option>
                                </select>
                            </div>
                        </div>

                        {/* Items Table */}
                        <div style={{ flex: 1, overflowY: 'auto', border: '1px solid #ddd', marginBottom: '10px' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                                <colgroup>
                                    <col style={{ width: '200px' }} />
                                    <col style={{ width: '80px' }} />
                                    <col style={{ width: '80px' }} />
                                    <col style={{ width: '100px' }} />
                                    <col style={{ width: '100px' }} />
                                    <col />
                                    <col style={{ width: '50px' }} />
                                </colgroup>
                                <thead style={{ position: 'sticky', top: 0, background: '#333', color: 'white', zIndex: 1 }}>
                                    <tr>
                                        <th style={{ padding: '8px' }}>항목명</th>
                                        <th style={{ padding: '8px' }}>단위</th>
                                        <th style={{ padding: '8px' }}>수량</th>
                                        <th style={{ padding: '8px' }}>단가</th>
                                        <th style={{ padding: '8px' }}>합계</th>
                                        <th style={{ padding: '8px' }}>비고</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {lineItems.map((item, idx) => (
                                        <tr key={idx} style={{ borderBottom: '1px solid #eee', background: item.quantity > 0 ? '#e3f2fd' : 'white' }}>
                                            <td style={{ padding: '4px' }}>
                                                {item.isCustom ?
                                                    <input type="text" value={item.name} onChange={e => handleRowChange(idx, 'name', e.target.value)} style={{ width: '100%' }} placeholder="항목명" /> :
                                                    <span style={{ fontWeight: item.quantity > 0 ? 'bold' : 'normal' }}>{item.name}</span>
                                                }
                                            </td>
                                            <td style={{ padding: '4px' }}><input type="text" value={item.unit} onChange={e => handleRowChange(idx, 'unit', e.target.value)} style={{ width: '100%', textAlign: 'center' }} /></td>
                                            <td style={{ padding: '4px' }}><input type="number" value={item.quantity} onChange={e => handleRowChange(idx, 'quantity', e.target.value)} style={{ width: '100%', textAlign: 'center', fontWeight: 'bold', border: item.quantity > 0 ? '2px solid #2196f3' : '1px solid #ddd' }} /></td>
                                            <td style={{ padding: '4px' }}><input type="number" value={item.unitPrice} onChange={e => handleRowChange(idx, 'unitPrice', e.target.value)} style={{ width: '100%', textAlign: 'right' }} /></td>
                                            <td style={{ padding: '4px', textAlign: 'right', fontWeight: 'bold' }}>{item.total.toLocaleString()}</td>
                                            <td style={{ padding: '4px' }}><input type="text" value={item.remarks} onChange={e => handleRowChange(idx, 'remarks', e.target.value)} style={{ width: '100%' }} /></td>
                                            <td style={{ padding: '4px', textAlign: 'center' }}>
                                                {item.isCustom && <button onClick={() => removeRow(idx)} style={{ color: 'red', border: 'none', background: 'none' }}>x</button>}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <button onClick={addCustomRow} style={{ width: '100%', padding: '8px', background: '#f1f3f5', border: 'none', marginBottom: '10px', cursor: 'pointer' }}>+ 항목 추가</button>

                        {/* Summary & Footer */}
                        <div style={{ display: 'flex', gap: '20px' }}>
                            <div style={{ flex: 1 }}>
                                <label style={{ fontSize: '12px', fontWeight: 'bold' }}>비고 / 조건 (Footer)</label>
                                <textarea value={remarks} onChange={e => setRemarks(e.target.value)} style={{ width: '100%', height: '100px', padding: '10px' }} />
                            </div>
                            <div style={{ width: '300px', padding: '15px', background: '#f8f9fa', borderRadius: '5px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                                    <span>공급가액</span>
                                    <span>{supplyPrice.toLocaleString()} 원</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                                    <span>부가세 ({vatIncluded ? '포함' : '10%'})</span>
                                    <span>{vat.toLocaleString()} 원</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '10px', paddingTop: '10px', borderTop: '2px solid #333', fontWeight: 'bold', fontSize: '18px' }}>
                                    <span>총 견적금액</span>
                                    <span>{total.toLocaleString()} 원</span>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {viewMode === 'create' && (
                    <div style={{ borderTop: '1px solid #ddd', paddingTop: '15px', marginTop: '10px', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                        <button onClick={onClose} style={{ padding: '10px 20px', cursor: 'pointer' }}>취소</button>
                        <button onClick={handleSave} disabled={isSaving} style={{ padding: '10px 20px', background: '#2c3e50', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
                            {isSaving ? '저장 중...' : '견적서 저장'}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default QuoteGenerator;
